import createApiInstance from '../../utils/createApiInstance';
import { VIDEOS_URL } from '../../constants/URLs';
import { AxiosResponse } from 'axios';
import { VideoInfo } from '../../../types/video/VideoInfo';
import { ServerPasteParams } from '../../../components/SharePanel/types/types';
import { SharePlayerInfo } from '../../../types/video/SharePlayerInfo';
import { VideoList } from '../../../types/video/VideoList';
import { AvailableVideoPropertiesToEdit } from '../../../types/video/availableVideoPropertiesToPatch';
import { FilterSettings } from '../../../components/VideoList/types/FilterSettings';
import {
    ThumbnailListResponse,
    CreateThumbnailResponse,
} from '../../../types/thumbnail/ThumbnailInfo';

const videosServiceInstance = createApiInstance(VIDEOS_URL);

export class VideosService {
    private constructor() {}

    static async getVideoList(
        params: {
            page?: number;
            size?: number;
        } & FilterSettings,
    ): Promise<VideoList> {
        const response: AxiosResponse = await videosServiceInstance.get('', { params });

        return response.data;
    }

    static async createVideoDocument(
        file: File,
        videoTitle: string,
        profile: string,
        enableSummarization: boolean,
    ): Promise<VideoInfo> {
        const response: AxiosResponse = await videosServiceInstance.post('', {
            profile,
            title: videoTitle,
            file_name: file.name,
            file_size: file.size,
            enable_summarization: enableSummarization,
        });

        return response.data;
    }

    /**
     * Backend API: https://a.yandex-team.ru/arcadia/videoplatform/cloud/vzhik/internal/handler/publicapi/video.go#L339
     */
    static async getDownloadUrl(contentId: string): Promise<{ download_url: string }> {
        const response: AxiosResponse = await videosServiceInstance.get(
            `/${contentId}/download-url`,
        );

        return response.data;
    }

    /**
     * Backend API: https://a.yandex-team.ru/arcadia/videoplatform/cloud/vzhik/internal/handler/publicapi/video.go?rev=r18783924#L228
     */
    static async editVideoDocument(
        contentId: string,
        editedProperties: AvailableVideoPropertiesToEdit,
    ): Promise<VideoInfo> {
        const response: AxiosResponse = await videosServiceInstance.patch(
            `/${contentId}`,
            editedProperties,
        );

        return response.data;
    }

    static async deleteVideoDocument(contentId: string) {
        await videosServiceInstance.delete(`/${contentId}`);
    }

    static async getPlayerUrl(
        contentId: string,
        params?: Record<ServerPasteParams, boolean>,
    ): Promise<SharePlayerInfo> {
        const response: AxiosResponse = await videosServiceInstance.get(
            `/${contentId}/player-url`,
            { params },
        );

        return response.data;
    }

    /**
     * Backend API: https://a.yandex-team.ru/arcadia/videoplatform/cloud/vzhik/internal/handler/publicapi/thumbnail.go?rev=r18783924#L27
     */
    static async listThumbnails(videoId: string): Promise<ThumbnailListResponse> {
        const response: AxiosResponse = await videosServiceInstance.get(`/${videoId}/thumbnails`);

        return response.data;
    }

    /**
     * Backend API: https://a.yandex-team.ru/arcadia/videoplatform/cloud/vzhik/internal/handler/publicapi/thumbnail.go?rev=r18783924#L92
     */
    static async createThumbnail(videoId: string): Promise<CreateThumbnailResponse> {
        const response: AxiosResponse = await videosServiceInstance.post(`/${videoId}/thumbnails`);

        return response.data;
    }

    /**
     * Backend API: https://a.yandex-team.ru/arcadia/videoplatform/cloud/vzhik/internal/handler/publicapi/thumbnail.go?rev=r18783924#L128
     */
    static async deleteCurrentThumbnail(videoId: string): Promise<void> {
        await videosServiceInstance.delete(`/${videoId}/thumbnails/current`);
    }
}
